import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import { EmotionProvider as EmotionThemeProvider } from '@tanium/react-emotion';
import { ThemeProvider as TaniumThemeProvider } from '@tanium/react-theme-context';
import { bluestar } from '@tanium/theme-data';
import { TitleBoldText } from '../../TextStyles';
import { TaniumLoginCard } from '../Login/TaniumLoginCard';
import {
  SeamPage,
  continueURL,
  isBrowserMismatch,
  isRateLimited,
  isServerError,
  markForwarded,
  wasForwarded,
} from './brokerSeam';

interface BrokerSeamPageProps {
  page: SeamPage;
  search: string;
  // Supplied by the composition root so a test can observe the forward instead
  // of navigating.
  navigate: (url: string) => void;
}

// How long the dispatch page waits for its forward to begin before it offers
// Continue by hand.
const FORWARD_START_TIMEOUT_MS = 5000;

const SeamMessage: React.VFC<{ title: string; body: string }> = ({ title, body }) => (
  <Box>
    <TitleBoldText>{title}</TitleBoldText>
    <Box mt={1}>{body}</Box>
  </Box>
);

// DispatchSeam forwards its flow to the continue leg. Continue is single-use,
// so a second one could only reach the invalid-link page: the page marks its
// history entry before it forwards, and a later load of that entry explains
// instead.
const DispatchSeam: React.VFC<{ target: string; navigate: (url: string) => void }> = ({ target, navigate }) => {
  const { t } = useTranslation();
  const [returned, setReturned] = useState(() => wasForwarded(window.history));
  const [forwarded, setForwarded] = useState(false);
  const [offered, setOffered] = useState(false);
  const leaving = useRef(false);

  // Declared before the forward below, so the beforeunload a navigation fires
  // as it begins is never missed. A restore from the back-forward cache runs
  // no effect again, so pageshow is what notices it.
  useEffect(() => {
    const onBeforeUnload = () => {
      leaving.current = true;
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setReturned(true);
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const forward = useCallback(() => {
    markForwarded(window.history);
    setForwarded(true);
    setOffered(false);
    navigate(target);
  }, [target, navigate]);

  // A script navigation, so the picker's form-action does not govern it: the
  // submission chain already ended when the browser landed on this page.
  useEffect(() => {
    if (!returned && !forwarded) {
      forward();
    }
  }, [returned, forwarded, forward]);

  // Continue stays disabled while a forward may be under way. A navigation
  // fires beforeunload before its request goes out, so a forward that has not
  // begun by the timeout did not happen, and Continue is offered by hand.
  useEffect(() => {
    if (!forwarded || offered) {
      return undefined;
    }
    const timer = window.setTimeout(() => setOffered(!leaving.current), FORWARD_START_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [forwarded, offered]);

  if (returned) {
    return (
      <SeamMessage
        title={t('This sign-in already continued')}
        body={t("This page already sent you to your organization's sign-in page...")}
      />
    );
  }
  return (
    <Box>
      <TitleBoldText>{t('Signing you in')}</TitleBoldText>
      <Box mt={1}>{t("Taking you to your organization's sign-in page.")}</Box>
      <Box mt={2}>
        <Button type="button" style={{ width: '100%' }} disabled={!offered} onClick={forward}>
          <Box as={Stack} justifyContent="center">
            {t('Continue')}
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

// ErrorSeam shows the copy for the failure the broker marked in search; an
// unmarked failure is a dead link.
const ErrorSeam: React.VFC<{ search: string }> = ({ search }) => {
  const { t } = useTranslation();
  if (isServerError(search)) {
    return <SeamMessage title={t('Something went wrong')} body={t('We could not complete your sign-in...')} />;
  }
  if (isBrowserMismatch(search)) {
    return (
      <SeamMessage
        title={t('This sign-in was started in another browser')}
        body={t('Return to the application you were using and start again, staying in one browser...')}
      />
    );
  }
  if (isRateLimited(search)) {
    return (
      <SeamMessage
        title={t('Too many sign-in attempts')}
        body={t('Wait a minute, then return to the application you were using and try again.')}
      />
    );
  }
  return <SeamMessage title={t('This sign-in link is invalid')} body={t('This sign-in link is not recognized...')} />;
};

// BrokerSeamPage renders outside the portal's authenticated shell: these pages
// carry no session and must not start one, and the error page has no flow id to
// tell the broker's login apart from the portal's own.
export const BrokerSeamPage: React.VFC<BrokerSeamPageProps> = ({ page, search, navigate }) => {
  const target = page === SeamPage.Dispatch ? continueURL(search) : null;

  return (
    <EmotionThemeProvider theme={bluestar}>
      <TaniumThemeProvider theme={bluestar}>
        <Box as={Stack} justifyContent="center" marginTop="150px">
          <TaniumLoginCard>
            {target !== null ? (
              <DispatchSeam target={target} navigate={navigate} />
            ) : (
              <ErrorSeam search={page === SeamPage.Error ? search : ''} />
            )}
          </TaniumLoginCard>
        </Box>
      </TaniumThemeProvider>
    </EmotionThemeProvider>
  );
};
